import './ModuleItem.scss';
import useSetPageTitle from 'utilities/useSetPageTitle';
import PageTabs from 'components/PageTabs/PageTabs';
import { AdminModuleTabs } from 'Routes';
import { useServices } from 'index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useChaptersStore } from 'stores/Admin/Editors/ModuleEditor/ChaptersStore';
import { useGeneralStore } from 'stores/Admin/Editors/ModuleEditor/GeneralStore';
import { useCertificationsStore } from 'stores/Admin/Editors/ModuleEditor/CertificationsStore';
import { Button } from '@trackman/web-shared-components';
import { NewAdminModule } from 'types/admin/modules';

type TabRouteKeys = keyof typeof AdminModuleTabs;

const tabNamesByRoutes: { [key in TabRouteKeys]: string } = {
  EditGeneral: 'General',
  EditChapters: 'Chapters',
  EditCertifications: 'Certifications',
};

export const ModuleItem = () => {
  const moduleId = useParams().moduleId ?? '';
  const { data, isPending } = useQuery({
    queryKey: ['adminModule', moduleId],
    queryFn: () => moduleListService.getModule(moduleId),
    enabled: !!moduleId,
  });

  const setInitialChapters = useChaptersStore((state) => state.setInitialChapters);
  const setInitialGeneralData = useGeneralStore((state) => state.setInitialData);
  const setInitialCertifications = useCertificationsStore((state) => state.setInitialCertifications);
  const resetChapters = useChaptersStore((state) => state.reset);
  const resetGeneralData = useGeneralStore((state) => state.reset);
  const resetCertifications = useCertificationsStore((state) => state.reset);
  const setInitializingGeneral = useGeneralStore((state) => state.setLoadingInitialData);
  const setInitializingChapters = useChaptersStore((state) => state.setLoadingInitialData);
  const setInitializingCertifications = useCertificationsStore((state) => state.setLoadingInitialData);
  const setLoadingGeneral = useGeneralStore((state) => state.setLoading);
  const setLoadingChapters = useChaptersStore((state) => state.setLoading);
  const setLoadingCertifications = useCertificationsStore((state) => state.setLoading);
  const loading = useGeneralStore((state) => state.loading);
  const title = useGeneralStore((state) => state.title);
  const shortDescription = useGeneralStore((state) => state.shortDescription);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const moduleListService = useServices().moduleListService;
  useSetPageTitle({ title: data?.title ?? title, subtitle: data?.shortDescription ?? shortDescription });

  const saveModule = useMutation({
    mutationFn: () =>
      !!moduleId
        ? moduleListService.updateModule({ ...saveModulePayload, id: moduleId })
        : moduleListService.createModule(saveModulePayload),
    onMutate: () => {
      setLoadingGeneral(true);
      setLoadingChapters(true);
      setLoadingCertifications(true);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['adminModuleList'] }),
        queryClient.invalidateQueries({ queryKey: ['learningHubModuleList'] }),
      ]);
    },
    onSettled: () => {
      setLoadingGeneral(false);
      setLoadingChapters(false);
      setLoadingCertifications(false);
    },
  });

  const navigateToModuleList = async () => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: ['adminModuleList'] })]);
    navigate('/Admin/ModuleList');
  };

  const saveModulePayload: NewAdminModule = {
    title: useGeneralStore((state) => state.title),
    shortDescription: useGeneralStore((state) => state.shortDescription),
    fullDescription: useGeneralStore((state) => state.fullDescription),
    imageId: useGeneralStore((state) => state.imageId),
    status: useGeneralStore((state) => state.status),
    chapters: useChaptersStore((state) => state.chapters),
    certifications: useCertificationsStore((state) => state.certifications),
  };

  useEffect(() => {
    setInitializingGeneral(isPending && !!moduleId);
    setInitializingChapters(isPending && !!moduleId);
    setInitializingCertifications(isPending && !!moduleId);
    if (!isPending && data) {
      setInitialChapters(data.chapters ?? []);
      setInitialCertifications(data.certifications ?? []);
      setInitialGeneralData({
        id: data.id,
        title: data.title,
        shortDescription: data.shortDescription,
        fullDescription: data.fullDescription,
        imageId: data.imageId,
        status: data.status,
      });
    }
  }, [isPending, data]);

  useEffect(
    () => () => {
      resetCertifications();
      resetChapters();
      resetGeneralData();
    },
    []
  );

  return (
    <PageTabs tabs={tabNamesByRoutes} defaultTabName={tabNamesByRoutes.EditGeneral}>
      <div className='buttons'>
        <Button variant='contained' intent='tertiary' onClick={() => navigateToModuleList()} disabled={loading}>
          Cancel
        </Button>
        <Button variant='contained' intent='primary' onClick={() => saveModule.mutate()} disabled={loading} isLoading={loading}>
          Save
        </Button>
      </div>
    </PageTabs>
  );
};
